'use strict';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { Alert, Box, CircularProgress, CssBaseline, Snackbar } from '@mui/material';
import api from './api/api';
import Login from './Pages/Login';
import POS from './Pages/POS';
import SinAcceso from './Pages/SinAcceso';
import { clearAuthSession, commitAuthSession, getTenantId, readAuthSession } from './auth/authStorage';

const PDV_PERMISSION = 'pdv:terminal:ver';

function normalizePermission(value) {
  if (typeof value === 'string') return value.trim().toLowerCase();
  return String(value?.name || value?.permission || value?.code || value?.clave || '').trim().toLowerCase();
}

function permissionsOf(tenant) {
  const source = Array.isArray(tenant?.permissions)
    ? tenant.permissions
    : Array.isArray(tenant?.permisos)
      ? tenant.permisos
      : [];
  return [...new Set(source.map(normalizePermission).filter(Boolean))];
}

function findTenant(payload, tenantid) {
  const rows = Array.isArray(payload?.allowedTenants)
    ? payload.allowedTenants
    : Array.isArray(payload?.allowed_tenants)
      ? payload.allowed_tenants
      : [];
  return rows.find((tenant) => String(getTenantId(tenant)) === String(tenantid)) || null;
}

function Loader() {
  return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
}

function RequirePOS({ authenticated, allowed, children }) {
  const location = useLocation();
  if (!authenticated) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (!allowed) return <Navigate to="/sin-acceso" replace />;
  return children;
}

function AppRoutes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [user, setUser] = useState(null);
  const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });
  const allowed = useMemo(() => permissions.includes(PDV_PERMISSION), [permissions]);

  const notify = useCallback((message, severity = 'info') => {
    setSnack({ open: true, message, severity });
  }, []);

  const verify = useCallback(async () => {
    setLoading(true);
    const session = readAuthSession();

    if (!session.token || !session.tenantid) {
      setAuthenticated(false);
      setPermissions([]);
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode(session.token);
      if (decoded?.exp && decoded.exp < Date.now() / 1000) throw new Error('TOKEN_EXPIRED');

      const { data } = await api.get('/auth/me', { timeout: 6000 });
      const tenant = findTenant(data, session.tenantid);
      if (!tenant) throw new Error('TENANT_ACCESS_NOT_FOUND');

      const currentPermissions = permissionsOf(tenant);
      const currentUser = data?.user || session.user || null;
      commitAuthSession({
        token: session.token,
        user: currentUser,
        tenant: { ...(session.tenant || {}), ...tenant, permissions: currentPermissions },
      });

      setUser(currentUser);
      setPermissions(currentPermissions);
      setAuthenticated(true);
    } catch (error) {
      const cached = permissionsOf(session.tenant || {});
      const recoverable = !error?.response || [408, 425, 429, 500, 502, 503, 504, 530].includes(Number(error?.response?.status || 0));

      if (recoverable && cached.length) {
        setUser(session.user || null);
        setPermissions(cached);
        setAuthenticated(true);
        notify('API no disponible: sesión iniciada con permisos previamente validados.', 'warning');
      } else {
        clearAuthSession();
        setAuthenticated(false);
        setPermissions([]);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { verify(); }, [verify]);

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          clearAuthSession();
          setAuthenticated(false);
          setPermissions([]);
          setUser(null);
          navigate('/login', { replace: true });
        }
        return Promise.reject(error);
      }
    );

    return () => api.interceptors.response.eject(interceptor);
  }, [navigate]);

  const handleLogin = useCallback(async ({ token, user: loginUser, tenant }) => {
    setLoading(true);
    try {
      const currentPermissions = permissionsOf(tenant);

      if (!currentPermissions.includes(PDV_PERMISSION)) {
        throw new Error('PDV_PERMISSION_DENIED');
      }

      const currentUser = loginUser || null;
      const normalizedTenant = {
        ...(tenant || {}),
        permissions: currentPermissions,
      };

      // Login.js ya obtuvo /auth/me y entregó el tenant seleccionado con sus
      // permisos. No repetimos /auth/me aquí: evita una segunda validación con
      // contexto de tenant y mantiene un único flujo de autenticación.
      commitAuthSession({ token, user: currentUser, tenant: normalizedTenant });

      setUser(currentUser);
      setPermissions(currentPermissions);
      setAuthenticated(true);
      navigate('/', { replace: true });
      notify('Sesión Punto de Venta iniciada.', 'success');
    } catch (error) {
      clearAuthSession();
      setAuthenticated(false);
      setPermissions([]);
      setUser(null);

      if (error?.message === 'PDV_PERMISSION_DENIED') {
        notify(`La empresa seleccionada no tiene el permiso ${PDV_PERMISSION}.`, 'error');
      } else {
        notify('No fue posible iniciar la sesión del Punto de Venta.', 'error');
      }

      navigate('/login', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate, notify]);

  const handleLogout = useCallback(() => {
    clearAuthSession();
    setAuthenticated(false);
    setPermissions([]);
    setUser(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  if (loading) return <Loader />;

  return (
    <>
      <Routes>
        <Route path="/login" element={authenticated ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />} />
        <Route path="/sin-acceso" element={<SinAcceso onSalir={handleLogout} permiso={PDV_PERMISSION} />} />
        <Route path="/" element={<RequirePOS authenticated={authenticated} allowed={allowed}><POS usuario={user} onSalir={handleLogout} /></RequirePOS>} />
        <Route path="/pos" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to={authenticated ? '/' : '/login'} replace />} />
      </Routes>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((current) => ({ ...current, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default function App() {
  return <BrowserRouter><CssBaseline /><AppRoutes /></BrowserRouter>;
}
